/* eslint-disable indent */
import dayjs from 'dayjs';
import {
  ROLES,
  TIMEZONE_VN,
  specialistSetting,
  institutionalSpecialistSetting,
  specialistFromContractSetting,
  institutionalSpecialistFromContractSetting,
  PROJECT_ROLES,
  PROGRAM_TEMPLATE,
  TEMPLATE,
  PROJECT_NEW_STATUSES,
  COUNCIL_STATUS,
  PROJECT_PROPOSAL_STATUSES,
  COUNCIL_TYPE_CODE,
  PROJECT_PROPOSAL_NEW_STATUSES,
} from 'constant';
import { PRODUCT_DETAIL_FIELD } from 'constant/summary-report.constant';
import { convertUnicodeToAscii } from './convert';

export * from './convert';
// eslint-disable-next-line import/no-cycle
export * from './error';
export * from './format';
export * from './downloadFile';

export const is1593 = (template) => {
  if (template) {
    if ([TEMPLATE[1593], TEMPLATE[1595]].includes(template)) {
      return true;
    }
    if (template === TEMPLATE[193]) {
      return false;
    }
  }
  return true; // Default to true if no template provided
};

export const getTemplatePrograms = () => [
  { value: PROGRAM_TEMPLATE[1595], name: 1595 },
  { value: PROGRAM_TEMPLATE[1593], name: 1593 },
  { value: PROGRAM_TEMPLATE[193], name: 193 },
];

export const getGenders = (t) => [
  { value: 'male', name: t('gender.male') },
  { value: 'female', name: t('gender.female') },
];

export const setExpenditureTableOfSummaryReport = (summaryReportExpenditure, name, type) => {
  const handler = summaryReportExpenditure?.filter(
    (item) => item?.summaryReportExpenditureType?.englishName === name
  );
  if (type === 'notes') {
    return handler?.[0]?.notes ?? null;
  }
  if (type === 'expenditure') {
    return handler?.[0]?.expenditure ?? null;
  }
  return null;
};

export const addHours = (date, hours) => {
  date.setHours(date.getHours() + hours);

  return date;
};

export const getFilterMembersAreAllowedReview = (members) => {
  const filterMembersAreAllowReview = members?.filter((member) => {
    const isAllowedReview = member?.councilMemberRole?.isAllowedReview;
    const isPresent = member?.isPresent;

    // Only include members who are allowed to review AND are present (isPresent !== false)
    return isAllowedReview && isPresent !== false;
  });

  return filterMembersAreAllowReview;
};

// Helper function to get all members allowed to review regardless of attendance
export const getFilterMembersAllowedReviewAnyAttendance = (members) => {
  const filterMembersAreAllowReview = members?.filter(
    (member) => member?.councilMemberRole?.isAllowedReview
  );

  return filterMembersAreAllowReview;
};

// Helper function to get members who are allowed to review, present, and have reviewed
export const getFilterMembersPresentAndReviewed = (members) => {
  const presentMembersWhoReviewed = members?.filter((member) => {
    const isAllowedReview = member?.councilMemberRole?.isAllowedReview;
    const isPresent = member?.isPresent;
    const hasReviewed = member?.projectProposalCouncilMembers?.length !== 0;
    return isAllowedReview && isPresent !== false && hasReviewed;
  });

  return presentMembersWhoReviewed;
};

// Helper function to count members who are present and have reviewed
export const getNumberMembersPresentAndReviewed = (members) => {
  if (!members?.length) return 0;
  return getFilterMembersPresentAndReviewed(members)?.length ?? 0;
};

export const getFilterAllowedReviewAndCouncilStatus = (members, statusCouncil) =>
  members?.filter((member) => {
    const isAllowedReview = member?.councilMemberRole?.isAllowedReview;
    const councilMember = member?.projectProposalCouncilMembers?.[0];
    const isSubmitted = councilMember?.isSubmitted;
    const isPresent = councilMember?.isPresent;

    // If statusCouncil is 'notReviewed', only allow review if isSubmitted is true
    // Also consider attendance: only include members who are present (isPresent !== false)
    return (
      isAllowedReview && isPresent !== false && !(statusCouncil === 'notReviewed' && !isSubmitted)
    );
  });

export const isMemberAllowedReview = (member) => member?.councilMemberRole?.isAllowedReview;

export const getOneReviewProjectProposalCouncilMembers = (
  projectProposalCouncilMembers,
  projectProposalId
) => {
  if (projectProposalCouncilMembers?.length) {
    const result = projectProposalCouncilMembers.filter(
      (row) => row.projectProposalId === projectProposalId
    );
    return result;
  }

  return [];
};

export const getEndingTimeOfDate = (date) => {
  date.setHours(23);
  date.setMinutes(59);
  date.setSeconds(59);

  return date;
};

export const addDaysToDate = (date = new Date(), daysToAdd = 0) => {
  date.setDate(date.getDate() + daysToAdd);
  return date;
};

export const getMaxDate = (...dates) => {
  const maxDateInMiliseconds = Math.max(...dates.map((date) => new Date(date || null).getTime()));
  return new Date(maxDateInMiliseconds);
};

export const getDirtyValues = (_dirtyFields, allValues) => {
  if (_dirtyFields === true || Array.isArray(_dirtyFields)) {
    return allValues;
  }

  // Object
  return Object.fromEntries(
    Object.keys(_dirtyFields).map((key) => [key, getDirtyValues(_dirtyFields[key], allValues[key])])
  );
};

export const removeFalsyValue = (obj) => {
  const newObj = { ...obj };
  Object.keys(newObj).forEach((key) => {
    if (!newObj[key]) {
      delete newObj[key];
    }
  });
  return newObj;
};

export const mapErrorDialog = (t, error) => {
  if (error?.code && !t(`errorMessage.${error?.code}`).startsWith('errorMessage')) {
    return t(`errorMessage.${error?.code}`);
  }
  return error?.message ?? '';
};

export const mapError = (t, error) => {
  const data = error?.response?.data;
  const errorMessage = typeof error === 'string' ? error : data?.type || data?.code;

  return t(`errorMessage.${errorMessage}`).startsWith('errorMessage')
    ? errorMessage
    : t(`errorMessage.${errorMessage}`);
};

export const modifyFilters = (filters) => {
  const newFilters = {};
  // eslint-disable-next-line no-restricted-syntax
  for (const [key, filterValue] of Object.entries(filters)) {
    newFilters[key] = filterValue.value;
  }
  return newFilters;
};

export const checkOverdueDatetime = (datetime) => {
  if (!datetime) return false;
  const deadline = new Date(datetime).toLocaleString('en-ZA', {
    timeZone: TIMEZONE_VN,
  });
  const currentTime = new Date().toLocaleString('en-ZA', { timeZone: TIMEZONE_VN });

  if (currentTime > deadline) {
    return true;
  }
  return false;
};

export const getContactOrganizationName = (organizations, contact, t) => {
  const organization = organizations?.find((item) => item?.id === contact?.organizationId) ?? null;

  if (organization?.englishName === 'Other') {
    return contact?.specificOrganizationName;
  }

  return t('lang') === 'vi' ? organization?.name : organization?.englishName;
};

export const getContactOrganizationNameV2 = (organization, contact, t) => {
  if (organization?.englishName === 'Other') {
    return contact?.specificOrganizationName;
  }

  return t('lang') === 'vi' ? organization?.name : organization?.englishName;
};

export const getOrganizationNameById = (id, organizations, specificOrganizationName, t) => {
  const organization = organizations?.find((item) => item?.id === id) ?? null;

  if (organization?.englishName === 'Other') return specificOrganizationName;

  return t('lang') === 'vi' ? organization?.name : organization?.englishName;
};

export const getAcadamicLevelShortHand = (academicRank, academicDegree, t, isShort) => {
  let rank = '';
  let degree = '';

  if (academicRank) {
    rank = isShort
      ? t(`academicRank.${academicRank}Short`)
      : `${t(`academicRank.${academicRank}`)},`;
  }

  if (academicDegree) {
    degree = `${t(`academicDegree.${academicDegree}`)},`;
    if (isShort) {
      degree = t(`academicDegree.${academicDegree}Short`);
    }
  }

  return `${`${rank} ${degree}`.trim()} `;
};

export const toNonAccentVietnamese = (str) => {
  let result = str;
  result = result.replace(/A|Á|À|Ã|Ạ|Â|Ấ|Ầ|Ẫ|Ậ|Ă|Ắ|Ằ|Ẵ|Ặ/g, 'A');
  result = result.replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, 'a');
  result = result.replace(/E|É|È|Ẽ|Ẹ|Ê|Ế|Ề|Ễ|Ệ/, 'E');
  result = result.replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, 'e');
  result = result.replace(/I|Í|Ì|Ĩ|Ị/g, 'I');
  result = result.replace(/ì|í|ị|ỉ|ĩ/g, 'i');
  result = result.replace(/O|Ó|Ò|Õ|Ọ|Ô|Ố|Ồ|Ỗ|Ộ|Ơ|Ớ|Ờ|Ỡ|Ợ/g, 'O');
  result = result.replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ/g, 'o');
  result = result.replace(/U|Ú|Ù|Ũ|Ụ|Ư|Ứ|Ừ|Ữ|Ự/g, 'U');
  result = result.replace(/ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ/g, 'u');
  result = result.replace(/Y|Ý|Ỳ|Ỹ|Ỵ/g, 'Y');
  result = result.replace(/ỳ|ý|ỵ|ỷ|ỹ/g, 'y');
  result = result.replace(/Đ/g, 'D');
  result = result.replace(/đ/g, 'd');
  // Some system encode vietnamese combining accent as individual utf-8 characters
  result = result.replace(/\u0300|\u0301|\u0303|\u0309|\u0323/g, ''); // Huyền sắc hỏi ngã nặng
  result = result.replace(/\u02C6|\u0306|\u031B/g, ''); // Â, Ê, Ă, Ơ, Ư
  return result;
};

export const toCamelCaseString = (str = '') => {
  if (!str) return '';

  const result = str.toLowerCase().replace(/^([A-Z])|[\s-_]+(\w)/g, (match, p1, p2) => {
    if (p2) return p2.toUpperCase();
    return p1.toLowerCase();
  });

  return result;
};

// eslint-disable-next-line arrow-body-style
export const paginationDetailString = (t, options, paginationData, pagination) => {
  return `${t('pagination.row')} ${
    paginationData.totalRecords !== options.first + 1
      ? `${t('pagination.from')} ${options.first + 1} ${t('pagination.to')} ${
          paginationData.totalRecords < options.first + pagination.rowsPerPage
            ? paginationData.totalRecords
            : options.first + pagination.rowsPerPage
        }`
      : `${options.first + 1}`
  } ${t('pagination.perTotal')} ${paginationData.totalRecords}`;
};

export const getNameCouncilType = (councilTypes, value) => {
  const councilType = councilTypes?.find((item) => item?.value === value) ?? null;

  return councilType?.name;
};

export const getNameRoleOfMemberInCouncil = (roles, value) => {
  const role = roles?.find((item) => item?.value === value) ?? null;
  return role?.name;
};

export const formatFileName = (fileName = '') => {
  if (!fileName) return '';
  const tokens = fileName.split('__');
  if (tokens?.length > 1) tokens.splice(0, 1);

  return tokens.join('__');
};

export const modifyReviewExpenditure = (reviewExpenditure, expenditureTypes) => {
  if (!(reviewExpenditure && expenditureTypes)) return [];
  let temp = [];
  if (typeof reviewExpenditure === 'string') {
    temp = JSON.parse(reviewExpenditure);
  }
  const result = temp.map(({ expenditureTypeId, ...rest }) => {
    const expenditureType = expenditureTypes.find((e) => e.id === expenditureTypeId);
    if (expenditureType) {
      return { ...rest, expenditureType };
    }
    return { ...rest, expenditureType: null };
  });

  return result;
};

export const removeLocalStorage = () => {
  localStorage.removeItem('admin-projects-revise-filters');
  localStorage.removeItem('admin-projects-revise-rowsPerPage');
  localStorage.removeItem('admin-employee-filters');
  localStorage.removeItem('admin-employee-rowsPerPage');
  localStorage.removeItem('admin-statistics-filters');
  localStorage.removeItem('admin-statistics-rowsPerPage');
  localStorage.removeItem('admin-users-statistics-filters');
  localStorage.removeItem('admin-projects-submit-filters');
  localStorage.removeItem('admin-councils/create-rowsPerPage');
  localStorage.removeItem('admin-councils-filters');
  localStorage.removeItem('admin-councils/create-filters');
  localStorage.removeItem('admin-councils-rowsPerPage');
  localStorage.removeItem('admin-dashboard-projects-revise-filters');
  localStorage.removeItem('admin-dashboard-projects-submit-filters');
  localStorage.removeItem('admin-dashboard-projects-submit-year-filters');
  localStorage.removeItem('admin-dashboard-projects-vertical-filters');
  localStorage.removeItem('admin-dashboard-projects-expenditrue-filters');
};

export const diff = (obj1, obj2) => {
  const result = {};
  if (Object.is(obj1, obj2)) {
    return false;
  }
  if (!obj2 || typeof obj2 !== 'object') {
    return obj2;
  }
  Object.keys(obj1 || {})
    .concat(Object.keys(obj2 || {}))
    .forEach((key) => {
      if (typeof obj2?.[key] === 'object' && typeof obj1?.[key] === 'object') {
        const value = diff(obj1?.[key], obj2?.[key]);
        if (value !== undefined && !Object.is(value, {})) {
          result[key] = value;
        } else result[key] = false;
      } else if (obj2?.[key] !== obj1?.[key] && !Object.is(obj1?.[key], obj2?.[key])) {
        result[key] = true;
      } else {
        result[key] = false;
      }
    });
  return result;
};

export const highlight = (isChanged, color = 'green', intensity = 200) =>
  isChanged ? `bg-${color}-${intensity} highlight` : '';

export const formatDate = (stringDate) =>
  stringDate ? new Date(stringDate).toLocaleDateString('en-GB', { timeZone: TIMEZONE_VN }) : null;

export const getAcademicRanks = (t) => [
  { value: 'professor', name: t('academicRank.professor') },
  { value: 'associateProfessor', name: t('academicRank.associateProfessor') },
];

export const getAcademicDegrees = (t) => [
  { value: 'doctorate', name: t('academicDegree.doctorate') },
  { value: 'master', name: t('academicDegree.master') },
  { value: 'bachelor', name: t('academicDegree.bachelor') },
  { value: 'university', name: t('academicDegree.university') },
  { value: 'associateDiploma', name: t('academicDegree.associateDiploma') },
  { value: 'highSchoolDiploma', name: t('academicDegree.highSchoolDiploma') },
  { value: 'doctor', name: t('academicDegree.doctor') },
  { value: 'specialistDoctor1', name: t('academicDegree.specialistDoctor1') },
  { value: 'specialistDoctor2', name: t('academicDegree.specialistDoctor2') },
];

export const getProjectTypes = (t, onlyABC = false) => [
  { value: 'A', name: t('projectTypes.A') },
  { value: 'B', name: t('projectTypes.B') },
  { value: 'C', name: t('projectTypes.C') },
  ...(!onlyABC
    ? [
        {
          value: 'scienceAndTechnologyProject',
          name: t('projectTypes.scienceAndTechnologyProject'),
        },
        { value: 'strongResearchTeam', name: t('projectTypes.strongResearchTeam') },
        {
          value: 'regularTasksAccordingToFunction',
          name: t('projectTypes.regularTasksAccordingToFunction'),
        },
        { value: 'maintainProject', name: t('projectTypes.maintainProject') },
        { value: 'CQG', name: t('projectTypes.CQG') },
      ]
    : []),
];

export const getDescription = (value, summaryReportKpis) => {
  for (let i = 0; i < summaryReportKpis?.length; i += 1) {
    if (value?.data?.projectProposalResearchKpis?.[0]?.id === summaryReportKpis[i]?.researchKpiId) {
      return summaryReportKpis[i]?.description;
    }
  }
  return '';
};

export function convertToPascalCase(text) {
  const withoutDiacritics = text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  const words = withoutDiacritics.split(' ');
  const pascalCaseText = words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('');

  return pascalCaseText;
}

export const getResearchKpiGroup = (researchKpis) => {
  const result = Object.values(
    researchKpis.reduce((acc, item) => {
      const groupId = item.researchKpiGroup.id;
      if (!acc[groupId]) {
        acc[groupId] = item.researchKpiGroup;
      }
      return acc;
    }, {})
  );
  return result;
};

export const formatCurrency = (number) =>
  new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(number);

export const getCodeCouncilTypeById = (councilTypes, id) => {
  const councilType = councilTypes?.find((item) => item?.id === id) ?? null;

  return councilType?.code;
};

export const toMillion = (number) => number / 1_000_000;

export const formatFileNameProduct = (fileName, projectCode, type, order) => {
  const fileNameSplit = fileName.split('.');
  const typeFile = fileNameSplit[fileNameSplit.length - 1];
  const orderUsed = order < 0 ? '' : `-${order}`;
  let result;
  switch (type) {
    case 'SPC':
      result = `${projectCode}-SanPhamCung-MC${orderUsed}.${typeFile}`;
      break;
    case 'SPM':
      result = `${projectCode}-SanPhamMem-MC${orderUsed}.${typeFile}`;
      break;
    case 'APKH':
      result = `${projectCode}-AnPhamKhoaHoc-MC${orderUsed}.${typeFile}`;
      break;
    case 'SHTT':
      result = `${projectCode}-SoHuuTriTue-MC${orderUsed}.${typeFile}`;
      break;
    case 'SPDT':
      result = `${projectCode}-SanPhamDaoTao-MC${orderUsed}.${typeFile}`;
      break;
    case 'CGCN':
      result = `${projectCode}-MC${orderUsed}.${typeFile}`;
      break;
    case 'PL':
      result = `${projectCode}-PhuLuc${orderUsed}.${typeFile}`;
      break;
    case 'KP':
      result = `${projectCode}-KinhPhi-MC${orderUsed}.${typeFile}`;
      break;
    default:
      break;
  }
  return convertUnicodeToAscii(result).replace(/\//g, '_');
};

export function getCouncilTypes(councilTypes, t) {
  return councilTypes.map((item) => ({
    id: item.id,
    name: t(`councilType.${item.code}`),
  }));
}

export const hasRole = (roles, role) => roles?.includes(role);

export const isValidSetting = (setting, projectType) =>
  setting.length === 0 || setting.includes(projectType);

export function checkIsAllowedCRUDCouncil(
  projectType,
  roles,
  settings = {
    specialist: specialistSetting,
    institutionalSetting: institutionalSpecialistSetting,
  }
) {
  const effectiveSpecialistSetting = settings?.specialist ?? specialistSetting;
  const effectiveInstitutionalSpecialistSetting =
    settings?.institutionalSetting ?? institutionalSpecialistSetting;

  if (hasRole(roles, ROLES.ADMIN)) {
    return true;
  }

  // If user is specialist
  if (
    isValidSetting(effectiveSpecialistSetting, projectType) &&
    hasRole(roles, ROLES.SPECIALIST) &&
    !hasRole(roles, ROLES.INSTITUSIONAL_SPECIALIST)
  ) {
    return true;
  }

  // If user is institutional specialist
  if (
    isValidSetting(effectiveInstitutionalSpecialistSetting, projectType) &&
    !hasRole(roles, ROLES.SPECIALIST) &&
    hasRole(roles, ROLES.INSTITUSIONAL_SPECIALIST)
  ) {
    return true;
  }

  // If user have both institutional specialist and specialist
  if (
    hasRole(roles, ROLES.SPECIALIST) &&
    hasRole(roles, ROLES.INSTITUSIONAL_SPECIALIST) &&
    (isValidSetting(effectiveSpecialistSetting, projectType) ||
      isValidSetting(effectiveInstitutionalSpecialistSetting, projectType))
  ) {
    return true;
  }

  return false;
}

export function checkIsAllowedCRUDFromContract(projectType, roles) {
  if (hasRole(roles, ROLES.ADMIN)) {
    return true;
  }
  if (
    isValidSetting(specialistFromContractSetting, projectType) &&
    hasRole(roles, ROLES.SPECIALIST) &&
    !hasRole(roles, ROLES.INSTITUSIONAL_SPECIALIST)
  ) {
    return true;
  }
  if (
    isValidSetting(institutionalSpecialistFromContractSetting, projectType) &&
    !hasRole(roles, ROLES.SPECIALIST) &&
    hasRole(roles, ROLES.INSTITUSIONAL_SPECIALIST)
  ) {
    return true;
  }
  if (
    hasRole(roles, ROLES.SPECIALIST) &&
    hasRole(roles, ROLES.INSTITUSIONAL_SPECIALIST) &&
    (isValidSetting(specialistFromContractSetting, projectType) ||
      isValidSetting(institutionalSpecialistFromContractSetting, projectType))
  ) {
    return true;
  }
  return false;
}

export function getRoles() {
  const userLocal = localStorage.getItem('admin-user')
    ? JSON.parse(localStorage.getItem('admin-user'))
    : null;

  const info = JSON.parse(atob(userLocal.accessToken.split('.')[1]));
  return info?.roles?.map((role) => role?.role?.code);
}

export function getHighestRoles() {
  const roles = getRoles();

  // if (hasRole(roles, ROLES.ADMIN)) {
  //   return ROLES.ADMIN;
  // }
  if (hasRole(roles, ROLES.DEPARTMENT_LEADER)) {
    return ROLES.DEPARTMENT_LEADER;
  }
  if (hasRole(roles, ROLES.SPECIALIST)) {
    return ROLES.SPECIALIST;
  }
  if (hasRole(roles, ROLES.INSTITUSIONAL_SPECIALIST)) {
    return ROLES.INSTITUSIONAL_SPECIALIST;
  }

  return null;
}

export function filterProjectTypes(projectTypes, roles) {
  if (hasRole(roles, ROLES.SPECIALIST) && !hasRole(roles, ROLES.INSTITUSIONAL_SPECIALIST)) {
    return specialistSetting.length === 0
      ? projectTypes
      : projectTypes.filter((item) => specialistSetting.includes(item.value));
  }
  if (hasRole(roles, ROLES.INSTITUSIONAL_SPECIALIST) && !hasRole(roles, ROLES.SPECIALIST)) {
    return institutionalSpecialistSetting.length === 0
      ? projectTypes
      : projectTypes.filter((item) => institutionalSpecialistSetting.includes(item.value));
  }
  if (hasRole(roles, ROLES.SPECIALIST) && hasRole(roles, ROLES.INSTITUSIONAL_SPECIALIST)) {
    return specialistSetting.length === 0 || institutionalSpecialistSetting.length === 0
      ? projectTypes
      : projectTypes.filter(
          (item) =>
            specialistSetting.includes(item.value) ||
            institutionalSpecialistSetting.includes(item.value)
        );
  }
  return projectTypes;
}

export const getMonth = (date) => {
  const month = new Date(date).getMonth();
  return month + 1;
};

export const getYear = (date) => {
  const year = new Date(date).getFullYear();
  return year;
};

export const getProductDetail = (t, productDetail) => {
  if (!productDetail) return '';
  const result = [];
  Object.keys(productDetail).forEach((key) => {
    result.push(
      `<div class="ml-2">- ${t(
        `submitDocument.product.productDetail.${PRODUCT_DETAIL_FIELD[key]}`
      )}: ${productDetail[key]} </div>`
    );
  });
  return result.join(' ');
};

export const modifyBindingProjectProposalGeneralData = (data) => {
  if (!data) return null;
  if (data?.oldMembers) {
    // eslint-disable-next-line no-param-reassign
    data.projectProposalContacts = data.projectProposalContacts.map((item) => ({
      ...item,
      reason: data?.oldMembers?.reasons?.find(
        (reasonItem) => reasonItem?.contact?.id === item?.contactId
      )?.content,
    }));
  }

  const owner = data.projectProposalContacts.find(
    (e) => PROJECT_ROLES.OWNER === e.projectRole.code
  );

  const modifiedOwner = {
    ...owner,
    workplace: data?.workplace ?? '',
    dob: owner?.dob ? new Date(owner?.dob) : null,
    dateOfIssue: owner?.dateOfIssue ? new Date(owner?.dateOfIssue) : null,
    researchLines: owner?.researchLines ?? [],
    scientificProfileUrl: owner?.contact?.user?.scientificProfileUrl,
  };

  const scientificSecretaries = data.projectProposalContacts
    .filter((e) => PROJECT_ROLES.SECRETARY === e.projectRole.code)
    ?.map((e) => ({
      contact: e.contact,
      researchLines: e?.researchLines ?? [],
      assignedTask: e.assignedTask,
      reason: e.reason,
      isAuthorized: e.isAuthorized,
      organizationId: e.organization?.id || e.organizationId,
      projectRoleId: e.projectRole.id,
      fullname: e?.fullname,
      gender: e?.gender,
      academicRank: e?.academicRank,
      academicDegree: e?.academicDegree,
      email: e?.email,
      specificOrganizationName: e?.specificOrganizationName,
    }));

  const mainParticipants = data.projectProposalContacts
    .filter((e) => PROJECT_ROLES.MAIN_PARTICIPANT === e.projectRole.code)
    ?.map((e) => ({
      contact: e.contact,
      researchLines: e?.researchLines ?? [],
      assignedTask: e.assignedTask,
      reason: e.reason,
      isAuthorized: e.isAuthorized,
      organizationId: e.organization?.id || e.organizationId,
      projectRoleId: e.projectRole.id,
      fullname: e?.fullname,
      gender: e?.gender,
      academicRank: e?.academicRank,
      academicDegree: e?.academicDegree,
      email: e?.email,
      specificOrganizationName: e?.specificOrganizationName,
    }));
  const participants = data.projectProposalContacts
    .filter((e) => PROJECT_ROLES.PARTICIPANT === e.projectRole.code)
    ?.map((e) => ({
      contact: e.contact,
      researchLines: e?.researchLines ?? [],
      assignedTask: e.assignedTask,
      reason: e.reason,
      isAuthorized: e.isAuthorized,
      organizationId: e.organization?.id || e.organizationId,
      projectRoleId: e.projectRole.id,
      fullname: e?.fullname,
      gender: e?.gender,
      academicRank: e?.academicRank,
      academicDegree: e?.academicDegree,
      email: e?.email,
      specificOrganizationName: e?.specificOrganizationName,
    }));
  const technicians = data.projectProposalContacts
    .filter((e) => PROJECT_ROLES.TECHNICIAN === e.projectRole.code)
    ?.map((e) => ({
      contact: e.contact,
      researchLines: e?.researchLines ?? [],
      assignedTask: e.assignedTask,
      reason: e.reason,
      isAuthorized: e.isAuthorized,
      organizationId: e.organization?.id || e.organizationId,
      projectRoleId: e.projectRole.id,
      fullname: e?.fullname,
      gender: e?.gender,
      academicRank: e?.academicRank,
      academicDegree: e?.academicDegree,
      email: e?.email,
      specificOrganizationName: e?.specificOrganizationName,
    }));

  const domesticExperts = data.projectProposalContacts
    .filter((e) => PROJECT_ROLES.DOMESTIC_EXPERT === e.projectRole.code)
    ?.map((e) => ({
      contact: e.contact,
      researchLines: e?.researchLines ?? [],
      assignedTask: e.assignedTask,
      reason: e.reason,
      isAuthorized: e.isAuthorized,
      organizationId: e.organization?.id || e.organizationId,
      projectRoleId: e.projectRole.id,
      fullname: e?.fullname,
      gender: e?.gender,
      academicRank: e?.academicRank,
      academicDegree: e?.academicDegree,
      email: e?.email,
      specificOrganizationName: e?.specificOrganizationName,
    }));
  const internationalExperts = data.projectProposalContacts
    .filter((e) => PROJECT_ROLES.INTERNATIONAL_EXPERT === e.projectRole.code)
    ?.map((e) => ({
      contact: e.contact,
      researchLines: e?.researchLines ?? [],
      assignedTask: e.assignedTask,
      reason: e.reason,
      isAuthorized: e.isAuthorized,
      organizationId: e.organization?.id || e.organizationId,
      projectRoleId: e.projectRole.id,
      fullname: e?.fullname,
      gender: e?.gender,
      academicRank: e?.academicRank,
      academicDegree: e?.academicDegree,
      email: e?.email,
      specificOrganizationName: e?.specificOrganizationName,
    }));

  return {
    vietnameseName: data.vietnameseName,
    englishName: data.englishName,
    projectType: data.projectType,
    pubProjectProposalId: data?.pubProjectProposalId,
    pubProjectProposal: data?.pubProjectProposal,
    owner: modifiedOwner,
    ownerContactId: modifiedOwner?.contact?.id,
    owners: [modifiedOwner],
    scientificSecretaries,
    mainParticipants,
    participants,
    technicians,
    domesticExperts,
    internationalExperts,
  };
};

export const modifyProjectProposalGeneralUpdateData = (data) => {
  // const projectProposalResearchFields = data.general?.projectProposalResearchFields
  //   .map((e, i) => ({ ...e, priority: i + 1 }))
  //   ?.filter((e) => e.researchFieldId);

  // const modifyRecommendedExperts = data.general?.recommendedExperts?.map((e) => ({
  //   ...e,
  //   phone: e.phone?.replace(/\s/g, '') ?? '',
  // }));

  const owner = {
    ...data.general.owner,
    dob: data.general?.owner?.dob ?? null,
    dateOfIssue: data.general?.owner?.dateOfIssue ?? null,
    citizenId: data.general?.owner?.citizenId?.replace(/\s/g, '') ?? '',
    taxIdentificationNumber: data.general?.owner?.taxIdentificationNumber?.replace(/\s/g, '') ?? '',
    bankAccount: data.general?.owner?.bankAccount?.replace(/\s/g, '') ?? '',
    mobile: data.general?.owner?.mobile?.replace(/\s/g, '') ?? '',
    researchLines: data.general?.owner?.researchLines ?? [],
  };
  delete owner.id;
  const projectProposalContacts = [
    ...data.general.owners.map((item) => ({ ...item, contactId: item.contact.id })),
    ...data.general.scientificSecretaries.map((item) => ({ ...item, contactId: item.contact.id })),
    ...data.general.mainParticipants.map((item) => ({ ...item, contactId: item.contact.id })),
    ...data.general.participants.map((item) => ({ ...item, contactId: item.contact.id })),
    ...data.general.technicians.map((item) => ({ ...item, contactId: item.contact.id })),
    ...data.general.domesticExperts.map((item) => ({ ...item, contactId: item.contact.id })),
    ...data.general.internationalExperts.map((item) => ({ ...item, contactId: item.contact.id })),
  ];

  // const projectProposalOrganizations = [
  //   {
  //     organizationId: data.general.lead.organizationId,
  //     name:
  //       data.general.lead.englishName === 'Other'
  //         ? data.general.lead.specificName
  //         : data.general.lead.name,
  //     englishName: data.general.lead.englishName,
  //     chief: data.general.lead.chief,
  //     phone: data.general.lead.phone,
  //     fax: data.general.lead.fax,
  //     email: data.general.lead.email,
  //     address: data.general.lead.address,
  //     bankAccount: data.general.lead.bankAccount,
  //     bank: data.general.lead.bank,
  //     type: 'LEAD',
  //   },
  //   ...data.general.partners.map((partner) => ({
  //     organizationId: partner.organizationId,
  //     name: partner?.englishName === 'Other' ? partner?.specificName : partner?.name,
  //     englishName: partner.englishName,
  //     chief: partner.chief,
  //     fax: partner.fax,
  //     phone: partner.phone,
  //     email: partner.email,
  //     address: partner.address,
  //     bankAccount: partner.bankAccount,
  //     bank: partner.bank,
  //     partnerContribution: partner.partnerContribution,
  //     type: 'PARTNER',
  //   })),
  // ];

  const newData = {
    ...data.general,
    // recommendedExperts: modifyRecommendedExperts,
    // projectProposalResearchFields,
    projectProposalContacts: [...projectProposalContacts],
    // ...data.general.researchExpense,
    // projectProposalOrganizations: [...projectProposalOrganizations],
    ownerContactId: data.general.ownerContactId,
    workplace: owner?.workplace,
  };
  delete newData.owner;

  delete newData.owners;
  delete newData.scientificSecretaries;
  delete newData.mainParticipants;
  delete newData.participants;
  delete newData.technicians;
  delete newData.domesticExperts;
  delete newData.internationalExperts;

  delete newData.lead;
  delete newData.partners;

  delete newData.researchExpense;
  delete newData?.pubProjectProposal;

  return newData;
};

export const formatTimeMarkerInPdf = (lastUpdated) => {
  const date = dayjs(lastUpdated);

  return date.format('YYMMmmDDHH');
};

export function appendToFormData(formData, key, data) {
  if (data === null || data === undefined) {
    formData.append(key, '');
  } else if (Array.isArray(data)) {
    data.forEach((item) => {
      appendToFormData(formData, `${key}`, item);
    });
  } else if (typeof data === 'object' && !(data instanceof File)) {
    Object.keys(data).forEach((subKey) => {
      appendToFormData(formData, `${key}.${subKey}`, data[subKey]);
    });
  } else {
    formData.append(key, data);
  }
}

export const getMissionTypesForReviews = (t) => [
  { value: 'research_topic', name: t('missionTypesForReviews.research_topic') },
  { value: 'project', name: t('missionTypesForReviews.project') },
  { value: 'production_project', name: t('missionTypesForReviews.production_project') },
];

export const getYearFromTemplate = (template, yearProposed) => {
  if (template) {
    if (template === TEMPLATE[1593] || template === TEMPLATE[1595]) {
      return 2025;
    }
    if (template === TEMPLATE[193]) {
      return 2019;
    }
  }
  return yearProposed;
};

export const modifyProjectProposalResearchResultReviewData = (researchResult, researchKpis) => {
  if (researchResult?.projectProposalResearchKpis === undefined) {
    return {
      softProducts: researchResult?.softProducts ?? [],
      hardProducts: researchResult?.hardProducts ?? [],
      qualityComparison: researchResult?.qualityComparison ?? '',
      knowledgeContributionLevel: researchResult?.knowledgeContributionLevel ?? '',
      practicalPolicyImpact: researchResult?.practicalPolicyImpact ?? '',
      teamDevelopmentLevel: researchResult?.teamDevelopmentLevel ?? '',
      researchTransferability: researchResult?.researchTransferability ?? '',
      projectProposalResearchKpis: [],
    };
  }

  let projectProposalResearchKpis = [];
  projectProposalResearchKpis =
    researchKpis?.map((kpi) => ({
      id: kpi.id,
      quantity: researchResult?.projectProposalResearchKpis?.[kpi.id]?.quantity ?? 0,
      content: researchResult?.projectProposalResearchKpis?.[kpi.id]?.content ?? '',
      researchKpi: {
        ...kpi,
      },
    })) ?? [];

  return {
    softProducts: researchResult?.softProducts ?? [],
    hardProducts: researchResult?.hardProducts ?? [],
    knowledgeContributionLevel: researchResult?.knowledgeContributionLevel ?? '',
    practicalPolicyImpact: researchResult?.practicalPolicyImpact ?? '',
    teamDevelopmentLevel: researchResult?.teamDevelopmentLevel ?? '',
    researchTransferability: researchResult?.researchTransferability ?? '',
    qualityComparison: researchResult?.qualityComparison ?? '',
    projectProposalResearchKpis,
  };
};

export const sanitizeObject = (obj) => {
  if (!obj || typeof obj !== 'object') {
    return {};
  }

  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => {
      // Check for null, undefined, empty string, empty array
      if (value === null || value === undefined || value === '') {
        return false;
      }

      if (Array.isArray(value) && value.length === 0) {
        return false;
      }

      // Check for objects like { min: null, max: null }
      if (typeof value === 'object' && !Array.isArray(value)) {
        return Object.values(value).some((v) => v !== null && v !== undefined && v !== '');
      }

      return true;
    })
  );
};

export const handleEnterKeyPress = (e, callback) => {
  if (e.key === 'Enter') {
    callback();
  }
};

export const addDefaultProjectStatusToFilter = (projectStatus) => {
  if (
    projectStatus?.length === 1 &&
    projectStatus[0] === PROJECT_NEW_STATUSES.HAS_REGISTER_RESULT
  ) {
    return Object.values(PROJECT_NEW_STATUSES);
  }

  if (projectStatus?.length) {
    return projectStatus;
  }

  return Object.values(PROJECT_NEW_STATUSES).filter(
    (status) => status !== PROJECT_NEW_STATUSES.HAS_REGISTER_RESULT
  );
};

export const getNewStatus = (projectProposal) => {
  const { status, projectProposalCouncils, version } = projectProposal;

  if (!status || !projectProposalCouncils) {
    return status?.name ?? 'new';
  }

  const hasCouncil = (code) => projectProposalCouncils.some((c) => c.councilType.code === code);
  const getCouncil = (code) => projectProposalCouncils.find((c) => c.councilType.code === code);

  // notQualified
  const latestCouncil = projectProposalCouncils[projectProposalCouncils.length - 1];
  const isNotQualifiedStatus = projectProposalCouncils.some(
    (council) => council.councilStatus === COUNCIL_STATUS.NOT_QUALIFIED
  );
  if (isNotQualifiedStatus && status.name === PROJECT_PROPOSAL_STATUSES.SUBMIT) {
    return PROJECT_PROPOSAL_NEW_STATUSES.NOT_QUALIFIED;
  }

  // approved
  if (
    status.name === PROJECT_PROPOSAL_STATUSES.APPROVED ||
    (latestCouncil?.councilStatus === COUNCIL_STATUS.QUALIFIED &&
      latestCouncil.councilType?.order === 3)
  ) {
    return PROJECT_PROPOSAL_NEW_STATUSES.APPROVED;
  }
  const financeCouncil = getCouncil(COUNCIL_TYPE_CODE.FINANCE_COUNCIL);
  if (
    financeCouncil?.councilStatus === COUNCIL_STATUS.QUALIFIED &&
    status.name === PROJECT_PROPOSAL_STATUSES.SUBMIT &&
    version === 4
  ) {
    return PROJECT_PROPOSAL_NEW_STATUSES.APPROVED;
  }

  // finance
  const specializationCouncil = getCouncil(COUNCIL_TYPE_CODE.SPECIALIZATION_COUNCIL);
  if (
    version === 3 &&
    ((financeCouncil && financeCouncil.councilStatus === COUNCIL_STATUS.NOT_REVIEWED) ||
      (specializationCouncil?.councilStatus === COUNCIL_STATUS.QUALIFIED && !financeCouncil))
  ) {
    return PROJECT_PROPOSAL_NEW_STATUSES.FINANCE;
  }

  // specialization
  const prequalificationCouncil = getCouncil(COUNCIL_TYPE_CODE.PRELIMINARY_SELECTION_COUNCIL);
  if (
    version === 2 &&
    !hasCouncil(COUNCIL_TYPE_CODE.FINANCE) &&
    ((specializationCouncil &&
      specializationCouncil.councilStatus === COUNCIL_STATUS.NOT_REVIEWED) ||
      (prequalificationCouncil?.councilStatus === COUNCIL_STATUS.QUALIFIED &&
        !specializationCouncil))
  ) {
    return PROJECT_PROPOSAL_NEW_STATUSES.SPECIALIZATION;
  }

  // prequalification
  if (
    prequalificationCouncil?.councilStatus === COUNCIL_STATUS.NOT_REVIEWED &&
    projectProposalCouncils.length === 1
  ) {
    return PROJECT_PROPOSAL_NEW_STATUSES.PREQUALIFICATION;
  }

  // submit
  if (status.name === PROJECT_PROPOSAL_STATUSES.SUBMIT && projectProposalCouncils.length === 0) {
    return PROJECT_PROPOSAL_NEW_STATUSES.SUBMIT;
  }

  // new
  if (status.name === PROJECT_PROPOSAL_STATUSES.NEW && projectProposalCouncils.length === 0) {
    return PROJECT_PROPOSAL_NEW_STATUSES.NEW;
  }

  return status.name;
};
